import React, { useState, useEffect, useCallback } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormHelperText,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Alert,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add,
  Edit,
  Delete,
  Search,
  Close,
  Notifications,
  Send,
  Class,
} from "@mui/icons-material";
import notificationService from "../../services/notificationService";
import api from "../../services/api";
import { fetchNotifications } from "../../store/slices/notificationSlice";
import PersistentDialog from "../../components/common/PersistentDialog";

const ClassNotifications = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const ALL_CLASSES_VALUE = "__all_classes__";
  const EMPTY_FORM = {
    title: "",
    message: "",
    type: "",
    classId: "",
    classIds: [],
    allClasses: false,
  };
  const [formData, setFormData] = useState(EMPTY_FORM);
  const resetForm = () => setFormData(EMPTY_FORM);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingNotification, setDeletingNotification] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [notificationTypesLoading, setNotificationTypesLoading] = useState(true);
  const [classesList, setClassesList] = useState([]);
  const [classFilter, setClassFilter] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [classesLoading, setClassesLoading] = useState(true);
  const [classNotifications, setClassNotifications] = useState([]);
  const [classNotificationsLoading, setClassNotificationsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [createFormErrors, setCreateFormErrors] = useState({ title: "", message: "", classIds: "", type: "" });
  const [editFormErrors, setEditFormErrors] = useState({ title: "", message: "" });

  useEffect(() => {
    let cancelled = false;
    setNotificationTypesLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams();
        if (user?.instituteId != null && user.instituteId !== "") params.set("instituteId", String(user.instituteId));
        const qs = params.toString();
        const res = await api.get(qs ? `/academic-info/notification-types/all?${qs}` : "/academic-info/notification-types/all");
        const list = res?.data?.data ?? res?.data ?? [];
        if (!Array.isArray(list) || cancelled) return;
        const next = list.map((x) => ({ key: x.key, label: x.label })).filter((x) => x.key && x.label);
        if (!cancelled) setNotificationTypes(next);
      } catch {
        if (!cancelled) setNotificationTypes([]);
      } finally {
        if (!cancelled) setNotificationTypesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.instituteId]);

  const teacherId = user?.teacherId;

  const fetchClassNotifications = useCallback(() => {
    setClassNotificationsLoading(true);
    notificationService.getClassNotifications({ limit: 100 }).then((res) => {
      const payload = res.data?.data ?? res.data ?? {};
      const list = payload?.notifications ?? [];
      setClassNotifications(Array.isArray(list) ? list : []);
    }).catch(() => setClassNotifications([])).finally(() => setClassNotificationsLoading(false));
  }, []);

  useEffect(() => {
    fetchClassNotifications();
    dispatch(fetchNotifications({ limit: 100 }));
  }, [dispatch, fetchClassNotifications]);

  useEffect(() => {
    let cancelled = false;
    setClassesLoading(true);
    // Use the notification-scoped classes endpoint so the picker matches exactly the
    // classes the user can notify (and does not require the separate class.read permission).
    notificationService.getNotificationClasses().then((res) => {
      if (cancelled) return;
      const data = res.data?.data ?? res.data ?? [];
      const arr = Array.isArray(data) ? data : (data?.classes ?? data?.list ?? data?.rows ?? []);
      const list = Array.isArray(arr) ? arr : [];
      setClassesList(list);
    }).catch(() => setClassesList([])).finally(() => {
      if (!cancelled) setClassesLoading(false);
    });
    return () => { cancelled = true; };
  }, [teacherId]);

  const assignedClasses = classesList.map((c) => {
    const baseName = c.name || c.className || `Class ${c.id}`;
    const displayName = c.section ? `${baseName} - ${c.section}` : baseName;
    return { id: c.id, name: displayName };
  });

  const classIdToName = Object.fromEntries(assignedClasses.map((c) => [String(c.id), c.name]));

  const currentUserId = user?.id ?? user?.userId;
  const allNotifications = (classNotifications || []).map((n) => {
    const creatorId = n.createdByUserId ?? n.created_by_user_id;
    const canEdit = creatorId != null && Number(creatorId) === Number(currentUserId);
    const resolvedClassIds = Array.isArray(n.classIds) && n.classIds.length > 0
      ? n.classIds
      : Array.isArray(n.metadata?.classIds) && n.metadata.classIds.length > 0
        ? n.metadata.classIds
        : n.scopeId != null
          ? [n.scopeId]
          : [];
    const classNames = resolvedClassIds
      .map((id) => classIdToName[String(id)] || `Class ${id}`)
      .filter(Boolean);
    return {
    id: n.id,
    title: n.title,
    message: n.message || "",
    type: n.type ?? "",
    scopeId: n.scopeId,
    scopeType: n.scopeType,
    classIds: resolvedClassIds,
    classNames,
    createdByUserId: creatorId,
    canEdit,
    class: classNames.length > 0 ? classNames.join(", ") : "-",
    sentAt: n.createdAt ? new Date(n.createdAt).toLocaleString() : "-",
    createdAt: n.createdAt,
    status: n.isRead ? "read" : "unread",
  };
  });

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const filteredNotifications = allNotifications.filter((n) => {
    const matchesSearch =
      !searchQuery ||
      [n.title, n.message, n.class].some((v) =>
        String(v || "").toLowerCase().includes(searchQuery.toLowerCase())
      );
    const matchesClass =
      !classFilter ||
      (n.classNames || []).includes(classFilter) ||
      n.class === classFilter;
    return matchesSearch && matchesClass;
  });

  const getTypeColor = (type) => {
    switch (type) {
      case "info":
        return "primary";
      case "warning":
        return "warning";
      case "success":
        return "success";
      case "error":
        return "error";
      case "urgent":
        return "error";
      default:
        return "default";
    }
  };

  const columns = [
    {
      field: "title",
      headerName: "Notification",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            sx={{
              bgcolor: `${getTypeColor(params.row.type)}.main`,
              width: 40,
              height: 40,
            }}
          >
            <Notifications />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(params.row.message || "").substring(0, 40)}
              {(params.row.message || "").length > 40 ? "…" : ""}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "class",
      headerName: "Class",
      width: 280,
      renderCell: (params) => {
        const names = params.row.classNames?.length
          ? params.row.classNames
          : params.value && params.value !== "-"
            ? [params.value]
            : [];
        if (names.length === 0) return "-";
        if (names.length === 1) {
          return <Chip icon={<Class />} label={names[0]} size="small" />;
        }
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
            {names.map((name) => (
              <Chip key={name} icon={<Class />} label={name} size="small" />
            ))}
          </Box>
        );
      },
    },
    {
      field: "type",
      headerName: "Type",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={getTypeColor(params.value)}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === "read" ? "default" : "primary"}
        />
      ),
    },
    {
      field: "sentAt",
      headerName: "Sent At",
      width: 200,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => {
        const canEdit = params.row.canEdit;
        if (!canEdit) return null;
        return (
          <Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEditingNotification(params.row);
                setFormData({
                  ...EMPTY_FORM,
                  title: params.row.title || "",
                  message: params.row.message || "",
                  type: params.row.type ?? "",
                  classId: params.row.scopeId ?? "",
                });
                setEditDialogOpen(true);
              }}
              title="Edit"
            >
              <Edit />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setDeletingNotification(params.row);
                setDeleteConfirmOpen(true);
              }}
              title="Delete"
            >
              <Delete />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const selectedClassLabels = () => {
    if (formData.allClasses) return "All classes";
    const ids = new Set((formData.classIds || []).map((id) => Number(id)));
    const names = assignedClasses.filter((c) => ids.has(Number(c.id))).map((c) => c.name);
    return names.length ? names.join(", ") : "";
  };

  const handleClassSelectionChange = (event) => {
    const raw = event.target.value;
    const selected = typeof raw === "string" ? raw.split(",") : raw;
    if (selected.includes(ALL_CLASSES_VALUE)) {
      setFormData({
        ...formData,
        allClasses: true,
        classIds: assignedClasses.map((c) => c.id),
      });
    } else {
      const classIds = selected.map((v) => Number(v)).filter((n) => Number.isFinite(n) && n > 0);
      setFormData({
        ...formData,
        allClasses: classIds.length > 0 && classIds.length === assignedClasses.length,
        classIds,
      });
    }
    if (createFormErrors.classIds) setCreateFormErrors((prev) => ({ ...prev, classIds: "" }));
  };

  const handleCreateNotification = async () => {
    const title = formData.title?.trim() || "";
    const message = formData.message?.trim() || "";
    const classIds = (formData.classIds || []).map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0);
    const errors = {
      title: !title ? "Title is required" : "",
      message: !message ? "Message is required" : "",
      classIds: !formData.allClasses && classIds.length === 0 ? "Please select at least one class" : "",
      type: !formData.type ? "Please select a type" : "",
    };
    setCreateFormErrors(errors);
    if (errors.title || errors.message || errors.classIds || errors.type) return;

    setCreateLoading(true);
    try {
      // Always send explicit class IDs scoped to the dropdown the teacher sees.
      // "All Classes" => every class currently listed in the dropdown (no out-of-scope classes,
      // no duplicates), instead of a broad allClasses flag that the backend expands more widely.
      const dropdownClassIds = assignedClasses
        .map((c) => Number(c.id))
        .filter((n) => Number.isFinite(n) && n > 0);
      const targetClassIds = formData.allClasses
        ? [...new Set(dropdownClassIds)]
        : [...new Set(classIds)];
      const payload = { title, message, type: formData.type, classIds: targetClassIds };
      const res = await notificationService.createClassNotificationsBulk(payload);
      const classCount = res?.data?.data?.classCount ?? targetClassIds.length;
      setSnackbar({
        open: true,
        message:
          classCount === 1
            ? "Notification sent successfully"
            : `Notification sent to ${classCount} classes successfully`,
        severity: "success",
      });
      setCreateDialogOpen(false);
      setFormData({ title: "", message: "", type: "", classIds: [], allClasses: false });
      setCreateFormErrors({ title: "", message: "", classIds: "", type: "" });
      fetchClassNotifications();
      dispatch(fetchNotifications({ limit: 100 }));
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to send notification",
        severity: "error",
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditNotification = async () => {
    const title = formData.title?.trim() || "";
    const message = formData.message?.trim() || "";
    const errors = {
      title: !title ? "Title is required" : "",
      message: !message ? "Message is required" : "",
    };
    setEditFormErrors(errors);
    if (!editingNotification?.id || errors.title || errors.message) return;

    setEditLoading(true);
    try {
      await notificationService.updateNotification(editingNotification.id, {
        title,
        message,
        type: formData.type,
      });
      setSnackbar({ open: true, message: "Notification updated successfully", severity: "success" });
      setEditDialogOpen(false);
      setEditingNotification(null);
      setEditFormErrors({ title: "", message: "" });
      fetchClassNotifications();
      dispatch(fetchNotifications({ limit: 100 }));
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to update notification",
        severity: "error",
      });
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteNotification = async () => {
    if (!deletingNotification?.id) return;
    setDeleteLoading(true);
    try {
      await notificationService.deleteNotification(deletingNotification.id);
      setSnackbar({ open: true, message: "Notification deleted successfully", severity: "success" });
      setDeleteConfirmOpen(false);
      setDeletingNotification(null);
      fetchClassNotifications();
      dispatch(fetchNotifications({ limit: 100 }));
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to delete notification",
        severity: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Class Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage notifications for your assigned classes
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setFormData({ title: "", message: "", type: "", classIds: [], allClasses: false });
            setCreateFormErrors({ title: "", message: "", classIds: "", type: "" });
            setCreateDialogOpen(true);
          }}
        >
          Create Notification
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          As a class teacher, you can send notifications to students in classes:{" "}
          <strong>
            {classesLoading ? "Loading…" : assignedClasses.map((c) => c.name).join(", ") || "No classes assigned"}
          </strong>
        </Typography>
      </Alert>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          {
            label: "Total Notifications",
            value: allNotifications.length,
            color: "primary.main",
          },
          {
            label: "Sent Today",
            value: allNotifications.filter((n) => n.createdAt && new Date(n.createdAt) >= todayStart).length,
            color: "success.main",
          },
          {
            label: "Unread",
            value: allNotifications.filter((n) => n.status === "unread").length,
            color: "warning.main",
          },
          {
            label: "My Classes",
            value: assignedClasses.length,
            color: "info.main",
          },
        ].map((stat) => (
          <Grid item xs={6} sm={3} key={stat.label}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: stat.color }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Notifications Table */}
      <Card>
        <CardContent>
          <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
            <TextField
              placeholder="Search notifications..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Class</InputLabel>
              <Select
                label="Class"
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
              >
                <MenuItem value="">All Classes</MenuItem>
                {assignedClasses.map((c) => (
                  <MenuItem key={c.id} value={c.name}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <DataGrid
            rows={filteredNotifications}
            columns={columns}
            loading={classNotificationsLoading}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={{ border: "none" }}
          />
        </CardContent>
      </Card>

      {/* Create Notification Dialog */}
      <PersistentDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setCreateFormErrors({ title: "", message: "", classIds: "", type: "" });
          resetForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Create Class Notification
            <IconButton onClick={() => { setCreateDialogOpen(false); setCreateFormErrors({ title: "", message: "", classIds: "", type: "" }); resetForm(); }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Title"
                value={formData.title}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value });
                  if (createFormErrors.title) setCreateFormErrors((prev) => ({ ...prev, title: "" }));
                }}
                placeholder="Notification title"
                error={!!createFormErrors.title}
                helperText={createFormErrors.title}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Message"
                multiline
                rows={3}
                value={formData.message}
                onChange={(e) => {
                  setFormData({ ...formData, message: e.target.value });
                  if (createFormErrors.message) setCreateFormErrors((prev) => ({ ...prev, message: "" }));
                }}
                placeholder="Enter your message..."
                error={!!createFormErrors.message}
                helperText={createFormErrors.message}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!createFormErrors.classIds}>
                <InputLabel required>Classes</InputLabel>
                <Select
                  multiple
                  value={formData.allClasses ? [ALL_CLASSES_VALUE] : (formData.classIds || [])}
                  label="Classes"
                  input={<OutlinedInput label="Classes" />}
                  renderValue={() => selectedClassLabels() || "Select class(es)"}
                  onChange={handleClassSelectionChange}
                  disabled={classesLoading || assignedClasses.length === 0}
                >
                  <MenuItem value={ALL_CLASSES_VALUE}>
                    <Checkbox checked={formData.allClasses} />
                    <ListItemText primary="Select All Classes" />
                  </MenuItem>
                  {assignedClasses.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      <Checkbox checked={formData.allClasses || (formData.classIds || []).includes(c.id)} />
                      <ListItemText primary={c.name} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {createFormErrors.classIds || "Choose one class, multiple classes, or all classes in your institute."}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth error={!!createFormErrors.type}>
                <InputLabel shrink>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  displayEmpty
                  disabled={notificationTypesLoading}
                  renderValue={(v) => (notificationTypesLoading ? "Loading…" : (!v ? "Select type" : (notificationTypes.find((x) => x.key === v)?.label ?? v)))}
                  onChange={(e) => {
                    setFormData({ ...formData, type: e.target.value });
                    if (createFormErrors.type) setCreateFormErrors((prev) => ({ ...prev, type: "" }));
                  }}
                >
                  <MenuItem value="" disabled>Select type</MenuItem>
                  {notificationTypes.map((t) => (
                    <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{!notificationTypesLoading && createFormErrors.type}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDialogOpen(false); setCreateFormErrors({ title: "", message: "", classIds: "", type: "" }); resetForm(); }}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={createLoading ? null : <Send />}
            disabled={createLoading}
            onClick={handleCreateNotification}
          >
            {createLoading ? "Sending…" : "Send Now"}
          </Button>
        </DialogActions>
      </PersistentDialog>
      {/* Edit Notification Dialog */}
      <PersistentDialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setEditingNotification(null); setEditFormErrors({ title: "", message: "" }); resetForm(); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Edit Notification
            <IconButton onClick={() => { setEditDialogOpen(false); setEditingNotification(null); setEditFormErrors({ title: "", message: "" }); resetForm(); }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Title"
                value={formData.title}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value });
                  if (editFormErrors.title) setEditFormErrors((prev) => ({ ...prev, title: "" }));
                }}
                placeholder="Notification title"
                error={!!editFormErrors.title}
                helperText={editFormErrors.title}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Message"
                multiline
                rows={3}
                value={formData.message}
                onChange={(e) => {
                  setFormData({ ...formData, message: e.target.value });
                  if (editFormErrors.message) setEditFormErrors((prev) => ({ ...prev, message: "" }));
                }}
                placeholder="Enter your message..."
                error={!!editFormErrors.message}
                helperText={editFormErrors.message}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth disabled>
                <InputLabel>Class</InputLabel>
                <Select value={formData.classId} label="Class">
                  <MenuItem value={formData.classId}>
                    {classIdToName[String(formData.classId)] || formData.classId}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel shrink>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  disabled={notificationTypesLoading}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  {notificationTypes.map((t) => (
                    <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setEditingNotification(null); setEditFormErrors({ title: "", message: "" }); resetForm(); }}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={editLoading ? null : <Edit />}
            disabled={editLoading}
            onClick={handleEditNotification}
          >
            {editLoading ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </PersistentDialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; if (!deleteLoading) { setDeleteConfirmOpen(false); setDeletingNotification(null); } }}
      >
        <DialogTitle>Delete Notification</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{deletingNotification?.title}&quot;? This will remove it for all recipients.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteConfirmOpen(false); setDeletingNotification(null); }} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteNotification} disabled={deleteLoading}>
            {deleteLoading ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClassNotifications;
